import * as Sentry from '@sentry/react';
import { yupResolver } from '@hookform/resolvers/yup';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Checkbox, Chip, FormControlLabel, FormGroup, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import Button from '../../../../components/Button/Button';
import Input from '../../../../components/Input/Input';
import MoveToCategoryModal from '../../../../components/MoveToCategoryModal/MoveToCategoryModal';
import RemoveItemModal from '../../../../components/RemoveItemModal/RemoveItemModal';
import Select from '../../../../components/Select/Select';
import Textarea from '../../../../components/Textarea/Textarea';
import db_config from '../../../../configs/db_config';
import { LANG_TEXT } from '../../../../constants/language';
import { useMenuContext } from '../../../../context/MenuContext';
import { useSettingsContext } from '../../../../context/SettingsContext';
import usePermission from '../../../../hooks/usePermission';
import { db } from '../../../../libs/firebase';
import { newCorrelationId } from '../../../../libs/apiFetch';
import { deleteDoc, doc, getDoc, setDoc, updateDoc, writeBatch } from '../../../../libs/firestore-wrapper';
import { uploadImageToFirebaseStorage } from '../../../../utils';
import { CustomFieldErrors, FoodItem, FormValuesFood, ICategoryDetails, ICustomizeGroupSettings, IFoodDetails, RecommendedFoodMeta } from '../../menu.types';
import { AddRecommendedModal } from '../AddRecommendedModal/AddRecommendedModal';
import CustomizeGroupSettings from '../CustomizeGroupSettings/CustomizeGroupSettings';
import ImageUploader from '../UploadImage/UploadImage';
import { createDefaultValues } from './functions';
import { buttonAddCustomizeSetting, buttonAddStyle, buttonRemoveStyle, useStyles } from './styles';
import { usePreorderEnabled } from '../../../../hooks/usePreorderEnabled';
import { useAuthContext } from '../../../../context/AuthContext/AuthContext';
import { getAllMenuTypes, getMenuTypePathSegment } from '../../../../context/UserMenuContext/userMenu.services';
import { useCategoriesContext } from '../../../../context/CategoriesContext';

const taxeRateOption = [
  { value: '8', label: '8%' },
  {
    value: '10',
    label: '10%',
  },
];

const foodTypeOptions = [
  { value: 'food', label: 'FOOD' },
  {
    value: 'drink',
    label: 'DRINK',
  },
];

function AddFood({
  data = null,
  categories,
  toggleDrawer,
  setToast,
  setOpenToast,
}: {
  data: any;
  categories: ICategoryDetails[];
  toggleDrawer: (open: boolean, data: any) => void;
  setToast: (v: { type: 'error' | 'success'; content: string }) => void;
  setOpenToast: (v: boolean) => void;
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const [defaultValues, setDefaultValues] = useState<IFoodDetails>(createDefaultValues());
  const { restaurantId, isAdminActionBlocked } = usePermission();
  const { enabledNewVersion } = usePreorderEnabled();

  const [customizeGroupSettings, setCustomizeGroupSettings] = useState<ICustomizeGroupSettings[]>([]);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openCategoryModal, setOpenCategoryModal] = useState<boolean>(false);
  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const [recommendedFoods, setRecommendedFoods] = useState<FoodItem[]>([]);
  const [openRecommendedModal, setOpenRecommendedModal] = useState<boolean>(false);
  const { currentSetting, stadiumData } = useSettingsContext();
  const maxRecommendQuantity = currentSetting?.max_recommend_quantity ?? 4;
  const { foodMenu, setFoodMenu } = useMenuContext();
  const { refetchData: refetchCategories } = useCategoriesContext();
  const { userInfo, getUserUid } = useAuthContext();

  useEffect(() => {
    const value = data ? data : createDefaultValues();
    if (enabledNewVersion && Array.isArray(value?.categories_menu_ids) && (!value.categories || value.categories.length === 0)) {
      value.categories = value.categories_menu_ids;
    }
    setDefaultValues(value);

    // Load recommended foods from data
    if (value.foodtruck_recommended_food && value.foodtruck_recommended_food.length > 0) {
      const recFoods = value.foodtruck_recommended_food
        .map((meta: RecommendedFoodMeta) => {
          const food = foodMenu.foods.find((f) => f.id === meta.recommended_food_id);
          if (food) {
            return {
              id: food.id,
              name: food.name ?? '',
              store: food.sku ?? '',
              image: Array.isArray(food.image_url) ? food.image_url[0] : food.image_url,
              price: food.price,
              banner: false,
              enabled: meta.enabled,
              order: meta.order,
              restaurantId: food.restaurantId || '',
            };
          }
          return null;
        })
        .filter((item: FoodItem | null): item is FoodItem => item !== null)
        .sort((a: FoodItem, b: FoodItem) => (a.order ?? 0) - (b.order ?? 0));
      setRecommendedFoods(recFoods);
    } else {
      setRecommendedFoods([]);
    }
  }, [data, foodMenu.foods]);

  const defaultFormValues = {
    image_url: defaultValues.image_url || '',
    sku: defaultValues.sku || '',
    tax_rate: defaultValues.tax_rate || '',
    name: defaultValues.name || '',
    categories: defaultValues.categories || [],
    enabled_note: defaultValues.enabled_note || false,
    is_alcoholic: defaultValues.is_alcoholic || false,
    price: defaultValues.price.toString() || '0',
    description: defaultValues.description || '',
    customize_group_settings: defaultValues.customize_group_settings || [],
    food_type: defaultValues.food_type || '',
    foodtruck_recommended_food: defaultValues.foodtruck_recommended_food || [],
    pre_order_quantity: defaultValues.pre_order_quantity || 0,
  };

  const schema = yup.object().shape({
    sku: yup.string().trim().required('TEXT_REQUIRED'),
    tax_rate: yup.string().trim().required('TEXT_REQUIRED'),
    food_type: yup.string().trim().required('TEXT_REQUIRED'),
    name: yup.string().trim().required('TEXT_REQUIRED'),
    price: yup.number().typeError('PRICE_MUST_BE_NUMBER').min(0, 'PRICE_MUST_BE_NUMBER').required('PRICE_MUST_BE_NUMBER'),
    categories: yup
      .array()
      .min(1, 'At least one category is required')
      .of(yup.string())
      .test('is-first-item-valid', 'First item must not be an empty string', function (value) {
        if (Array.isArray(value) && value.length > 0) {
          const firstItem = value[0];
          return typeof firstItem === 'string' && firstItem !== '';
        }
        return true;
      })
      .required('TEXT_REQUIRED'),
    customize_group_settings: yup
      .array()
      .of(
        yup.object().shape({
          name: yup.string().trim().required('TEXT_REQUIRED'),
          price: yup.number().typeError('PRICE_MUST_BE_NUMBER').min(0, 'PRICE_MUST_BE_NUMBER').required('PRICE_MUST_BE_NUMBER'),
        })
      )
      .required('TEXT_REQUIRED'),
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
  } = useForm<FormValuesFood>({
    defaultValues: defaultFormValues,
    resolver: yupResolver(schema) as any,
  });

  const categoryIds = useWatch({ control, name: 'categories' });

  useEffect(() => {
    let value = data ? data : createDefaultValues();
    value.price = value.price.toString();
    value.customize_group_settings = value.customize_group_settings.map((item: any) => ({ ...item, price: item.price.toString() }));
    if (enabledNewVersion) {
      value.categories = value.categories_menu_ids ?? [];
    }
    reset(value);
    setDefaultValues(value);
    setCustomizeGroupSettings(value.customize_group_settings);
  }, [reset, data]);

  const inputFields = [
    { name: 'sku', label: 'ITEM_ID', type: 'input' },
    { name: 'name', label: 'FOOD_NAME', type: 'input' },
    { name: 'categories', label: 'CATEGORY' },
    { name: 'food_type', label: 'FOOD_TYPE', type: 'select', options: foodTypeOptions },
    { name: 'price', label: 'PRICE', type: 'input', disabled: isAdminActionBlocked && data?.price },
    { name: 'tax_rate', label: 'TAX_RATE', type: 'select', options: taxeRateOption },
    { name: 'pre_order_quantity', label: 'PRE_ORDER_QUANTITY', type: 'input', helpText: 'PRE_ORDER_QUANTITY_HELPER_TEXT', hidden: !enabledNewVersion },
    { name: 'description', label: 'DESCRIPTION', type: 'textarea' },
    { name: 'enabled_note', label: 'ENABLED_NOTE', type: 'checkbox' },
    { name: 'is_alcoholic', label: 'ALCOHOLIC_ITEM', checkboxLabel: 'ALCOHOLIC_ITEM_CHECKBOX', type: 'checkbox' },
  ];

  const handleAddCustomizeSetting = () => {
    setCustomizeGroupSettings((prevSettings) => [...prevSettings, { name: '', price: '0', enabled_note: false }]);
  };

  const updateFoodInStateAndDatabase = async (id: string, mergedData: IFoodDetails, data: FormValuesFood | null) => {
    const updateFoodItem = (foodItem: any) => {
      return foodItem.id === id ? mergedData : foodItem;
    };

    const sortFoodsByCreateTimeDesc = (foods: IFoodDetails[]) => {
      return foods.sort((a, b) => {
        if (a.create_time && b.create_time) {
          const aTime = a.create_time.seconds * 1000 + a.create_time.nanoseconds / 1000000;
          const bTime = b.create_time.seconds * 1000 + b.create_time.nanoseconds / 1000000;
          return bTime - aTime;
        } else if (a.create_time) {
          return -1;
        } else if (b.create_time) {
          return 1;
        } else {
          return 0;
        }
      });
    };

    setFoodMenu((prevStates) => {
      let updatedFoods;
      if (data) {
        updatedFoods = prevStates.foods.map(updateFoodItem);
      } else {
        updatedFoods = [mergedData, ...prevStates.foods];
      }
      return {
        ...prevStates,
        foods: sortFoodsByCreateTimeDesc(updatedFoods),
      };
    });

    const foodDocRef = doc(db, db_config.DB_TABLE_ALL_FOOD_DETAILS, id);
    if (data) {
      await updateDoc(foodDocRef, mergedData as any);
    } else {
      await setDoc(foodDocRef, mergedData);
    }
  };

  const updateCategoriesWithFoodIds = async (mergedData: IFoodDetails, id: string, remove?: boolean) => {
    const batch = writeBatch();

    await Promise.all(
      mergedData.categories.map(async (categoryId) => {
        const categoryRef = doc(db, db_config.DB_TABLE_ALL_CATEGORIES, categoryId);
        const categorySnapshot = await getDoc(categoryRef);
        const foodIds = categorySnapshot.data()?.foodIds || [];

        const updatedFoodIds = remove ? foodIds.filter((foodId: string) => foodId !== id) : [...foodIds, id];

        batch.update(categoryRef, { foodIds: updatedFoodIds, updated_by: getUserUid(), correlation_id: (mergedData as any).correlation_id });
      })
    );

    await batch.commit();
  };

  const updateUserMenuCategoriesWithFoodIds = async (mergedData: IFoodDetails, id: string, remove?: boolean) => {
    const batch = writeBatch();

    await Promise.all(
      (mergedData?.categories_menu_ids || []).map(async (categoryId) => {
        const categoryRef = doc(db, db_config.DB_TABLE_USER_MENU_CATEGORIES, categoryId);
        const categorySnapshot = await getDoc(categoryRef);
        if (!categorySnapshot.exists()) {
          return;
        }

        const foodIds = categorySnapshot.data()?.foodIds || [];
        const updatedFoodIds = remove ? foodIds.filter((foodId: string) => foodId !== id) : foodIds.includes(id) ? foodIds : [...foodIds, id];
        batch.update(categoryRef, { foodIds: updatedFoodIds, updated_by: getUserUid(), correlation_id: (mergedData as any).correlation_id });
      })
    );

    await batch.commit();

    const stadiumId = userInfo?.stadium_id ?? stadiumData?.id;

    try {
      if (!stadiumId) return;

      const menuTypes = getAllMenuTypes();

      const batch2 = writeBatch();

      await Promise.all(
        (mergedData?.categories_menu_ids || []).map(async (categoryId: string) => {
          for (const menuType of menuTypes) {
            const menuTypePath = getMenuTypePathSegment(menuType as any);
            const categoryRef = doc(db, `${db_config.DB_TABLE_USER_MENU_LIST}/${stadiumId}/${menuTypePath}/${categoryId}`);
            const categorySnapshot = await getDoc(categoryRef);
            const currentFoodIds = categorySnapshot.data()?.food_ids || [];

            if (remove) {
              let updatedFoodIds = currentFoodIds.filter((f: any) => f.id !== id);

              updatedFoodIds = updatedFoodIds.map((f: any) => {
                if (Array.isArray(f.recommended_food) && f.recommended_food.length > 0) {
                  const newRecommended = f.recommended_food.filter((rf: any) => rf.id !== id).map((rf: any, idx: number) => ({ ...rf, order: idx }));
                  return { ...f, recommended_food: newRecommended };
                }
                return f;
              });

              updatedFoodIds = updatedFoodIds.map((f: any, idx: number) => ({ ...f, order: idx }));

              batch2.set(
                categoryRef,
                { food_ids: updatedFoodIds, updated_by: getUserUid(), correlation_id: (mergedData as any).correlation_id },
                { merge: true }
              );
            } else {
              const exists = currentFoodIds.find((f: any) => f.id === id);
              if (!exists) {
                const newItem = { id: id, banner: false, enabled: false, order: currentFoodIds.length, recommended_food: [] };
                const newFoodIds = [...currentFoodIds, newItem];
                batch2.set(
                  categoryRef,
                  { food_ids: newFoodIds, updated_by: getUserUid(), correlation_id: (mergedData as any).correlation_id },
                  { merge: true }
                );
              }
            }
          }
        })
      );

      await batch2.commit();
    } catch (err) {
      console.error('Error updating user_menu_list for food ids', err);
      Sentry.captureException(err, { tags: { food_id: id }, extra: { stadiumId } });
    }
  };

  const onSubmit = handleSubmit(async (food: FormValuesFood) => {
    try {
      if (food.image_url.startsWith('data:image')) {
        const uploadedUrl = await uploadImageToFirebaseStorage(food.image_url, restaurantId, defaultValues.id);
        food.image_url = uploadedUrl.download_url;
      }
      let mergedData = { ...defaultValues, ...food };
      if (isAdminActionBlocked && data?.price !== undefined) {
        mergedData.price = data.price;
      } else if (typeof mergedData.price === 'string') {
        mergedData.price = parseFloat(mergedData.price);
      }
      mergedData.customize_group_settings = mergedData.customize_group_settings.map((group) => ({
        ...group,
        price: typeof group.price === 'string' ? parseFloat(group.price) : group.price,
      }));

      mergedData.foodtruck_recommended_food = recommendedFoods.map((food, index) => ({
        recommended_food_id: food.id,
        enabled: true,
        order: index,
      }));

      if (!mergedData.restaurantId) {
        mergedData.restaurantId = restaurantId;
      }
      (mergedData as any).updated_by = getUserUid();
      (mergedData as any).correlation_id = newCorrelationId();
      if (enabledNewVersion) {
        mergedData.categories_menu_ids = mergedData.categories;
        mergedData.categories = [];
      }
      const id = defaultValues.id;
      await updateFoodInStateAndDatabase(id, mergedData, data);
      if (enabledNewVersion) {
        await updateUserMenuCategoriesWithFoodIds(mergedData, id);
        await refetchCategories();
      } else {
        await updateCategoriesWithFoodIds(mergedData, id);
        await refetchCategories();
      }
      setToast({ content: t(LANG_TEXT.UPDATE_SUCCESS_MESSAGE), type: 'success' });
    } catch (ex) {
      Sentry.captureException(ex, { tags: { food_id: defaultValues.id }, extra: { restaurantId } });
      setToast({ content: t(LANG_TEXT.UPDATE_FAILED_MESSAGE), type: 'error' });
    } finally {
      setOpenToast(true);
      toggleDrawer(false, null);
    }
  });

  const handleRemoveFood = async () => {
    try {
      if (data) {
        (data as any).correlation_id = newCorrelationId();
      }

      if (data && data.id) {
        setFoodMenu((prevStates) => ({
          ...prevStates,
          foods: prevStates.foods.filter((food) => food.id !== data.id),
        }));
        await deleteDoc(doc(db, db_config.DB_TABLE_ALL_FOOD_DETAILS, data.id));
      }

      if (enabledNewVersion) {
        await updateUserMenuCategoriesWithFoodIds(data, data.id, true);
        await refetchCategories();
      } else {
        await updateCategoriesWithFoodIds(data, data.id, true);
        await refetchCategories();
      }

      setToast({ content: t(LANG_TEXT.UPDATE_SUCCESS_MESSAGE), type: 'success' });
    } catch (error) {
      Sentry.captureException(error, { tags: { food_id: data?.id }, extra: { restaurantId } });
      setToast({ content: t(LANG_TEXT.UPDATE_FAILED_MESSAGE), type: 'error' });
    } finally {
      setOpenToast(true);
      toggleDrawer(false, null);
    }
  };

  return (
    <form className={classes.Box} role="presentation" name="add-food-form" noValidate autoComplete="off" onSubmit={onSubmit}>
      <Box className={classes.BoxHeader}>
        <Box className={classes.LeftHeaderBox}>
          <Typography fontSize={20} textAlign={'center'} fontWeight={500} lineHeight={'30px'} color={'var(--Gray-800, #1D2939)'}>
            {data ? t(LANG_TEXT.EDIT_ITEM) : t(LANG_TEXT.ADD_NEW)}
          </Typography>
        </Box>
        <Box className={classes.IconClose} onClick={() => toggleDrawer(false, null)}>
          <CloseIcon />
        </Box>
      </Box>
      <Box className={classes.BoxContent}>
        <Box className={classes.BoxItemInformation}>
          <Typography fontSize={18} fontWeight={600} lineHeight={'28px'} color={'var(--Gray-700, #344054)'}>
            {t(LANG_TEXT.ITEM_INFORMATION)}
          </Typography>
          <Box className={classes.ProductCard}>
            <Box className={classes.BoxInformationEachItem}>
              <Typography fontSize={14} fontWeight={500} lineHeight={'20px'} color={'var(--Gray-700, #344054)'}>
                {t(LANG_TEXT.ITEM_PHOTO)}
              </Typography>
              <ImageUploader
                imageUrl={defaultValues.image_url}
                onImageChange={(imageUrl) => {
                  setDefaultValues((prevDefaultValues) => ({
                    ...prevDefaultValues,
                    image_url: imageUrl,
                  }));
                  setValue('image_url', imageUrl);
                }}
                onRemoveImage={() => {
                  setValue('image_url', '');
                  setDefaultValues((prevDefaultValues) => ({
                    ...prevDefaultValues,
                    image_url: '',
                  }));
                }}
              />
            </Box>
            {inputFields
              .filter((field) => !field.hidden)
              .map((field) => (
                <Box key={field.name} className={classes.BoxInformationEachItem}>
                  <Typography fontSize={14} fontWeight={500} lineHeight={'20px'} color={'var(--Gray-700, #344054)'}>
                    {t(`${field.label}`)}
                  </Typography>
                  {field.type === 'input' && (
                    <Input
                      control={control}
                      error={(errors as CustomFieldErrors)?.[field.name]?.message || ''}
                      name={field.name.toString()}
                      placeholder={t('PRE_ORDER_QUANTITY_PLACEHOLDER')}
                      disabled={field?.disabled || false}
                      type={field.name === 'price' || field.name === 'pre_order_quantity' ? 'number' : 'text'}
                      startAdornment={field.name === 'price' ? true : false}
                    />
                  )}
                  {field.type === 'select' && field.name !== 'categories' && (
                    <Select
                      control={control}
                      name={field.name}
                      options={field.options || []}
                      disabled={field?.disabled || false}
                      multiple={field.name === 'categories' ? true : false}
                      error={(errors as CustomFieldErrors)?.[field.name]?.message || ''}
                    />
                  )}
                  {field.name === 'categories' && (
                    <>
                      <Box
                        onClick={() => setOpenCategoryModal(true)}
                        sx={{
                          width: '100%',
                          padding: '12px 14px',
                          border: '1px solid #D0D5DD',
                          borderRadius: '8px',
                          minHeight: '44px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: '8px',
                          '&:hover': {
                            borderColor: '#98A2B3',
                          },
                          '&:focus-within': {
                            borderColor: '#3D9F4A',
                            boxShadow: '0 0 0 4px rgba(61, 159, 74, 0.1)',
                          },
                        }}
                      >
                        {categoryIds.length > 0 ? (
                          categoryIds.map((catId) => {
                            const category = categories.find((c) => c.id === catId);
                            return category ? <Chip key={catId} label={category.title} /> : null;
                          })
                        ) : (
                          <Typography sx={{ color: '#667085', fontSize: '14px' }}>{t(LANG_TEXT.CATEGORIES)}</Typography>
                        )}
                      </Box>
                      {(errors as CustomFieldErrors)?.categories?.message && (
                        <Typography sx={{ color: '#F04438', fontSize: '12px', marginTop: '4px' }}>
                          {t((errors as CustomFieldErrors)?.categories?.message || '')}
                        </Typography>
                      )}
                      <MoveToCategoryModal
                        open={openCategoryModal}
                        handleClose={() => setOpenCategoryModal(false)}
                        categories={categories}
                        selectedCategoryIds={categoryIds}
                        onSelectCategory={(ids) => setValue('categories', ids)}
                        onConfirm={() => setOpenCategoryModal(false)}
                      />
                    </>
                  )}
                  {field.type === 'textarea' && <Textarea control={control} name={field.name} placeholder={t(field.label)} />}
                  {field.type === 'checkbox' && (
                    <Controller
                      name={field.name as any}
                      control={control}
                      render={({ field: { onChange, onBlur, value, ref } }) => (
                        <FormGroup>
                          <FormControlLabel
                            control={
                              <Checkbox
                                disabled={field?.disabled || false}
                                checked={Boolean(value ?? false)}
                                onChange={(e) => onChange(e.target.checked)}
                                onBlur={onBlur}
                                inputRef={ref}
                              />
                            }
                            label={t(field.checkboxLabel || field.label)}
                          />
                        </FormGroup>
                      )}
                    />
                  )}
                  {field.helpText && (
                    <Typography fontSize={10} lineHeight={'20px'} color={'var(--Gray-500, #667085)'}>
                      {t(field.helpText)}
                    </Typography>
                  )}
                </Box>
              ))}

            <Box className={classes.BoxInformationEachItem}>
              <Box className={classes.BoxAddCustomizeGroupSettings}>
                <Typography fontSize={14} fontWeight={500} lineHeight={'24px'} color={'var(--Gray-700, #344054)'}>
                  {t(LANG_TEXT.OPTION)}
                </Typography>
                <Button
                  id="add_customize_setting"
                  fullWidth={false}
                  title={t(LANG_TEXT.ADD_NEW)}
                  buttonStyle={buttonAddCustomizeSetting}
                  sxTextStyle={{ color: 'var(--Base-White, #FFF)', fontWeight: 600, fontSize: 14, lineHeight: '20px' }}
                  boxStyle={{
                    width: 'max-content !important',
                  }}
                  type="button"
                  onClick={handleAddCustomizeSetting}
                  startIcon={<AddIcon sx={{ color: 'rgba(255, 255, 255, 1)' }} />}
                />
              </Box>
              <Box className={classes.BoxGroupOption}>
                {customizeGroupSettings.map((option, index) => (
                  <CustomizeGroupSettings
                    key={index}
                    index={index}
                    control={control}
                    setCustomizeGroupSettings={setCustomizeGroupSettings}
                    errors={errors}
                    setValue={setValue}
                  />
                ))}
              </Box>
            </Box>

            <Box className={classes.BoxInformationEachItem}>
              <Typography fontSize={14} fontWeight={500} lineHeight={'24px'} color={'var(--Gray-700, #344054)'} sx={{ mb: 1 }}>
                {t(LANG_TEXT.RECOMMENDED_FOOD)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                {recommendedFoods.map((food, index) => (
                  <Box
                    key={food.id}
                    sx={{
                      position: 'relative',
                      width: 80,
                      height: 80,
                      border: '2px dashed #10B981',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      bgcolor: '#F0FDF4',
                      '&:hover .delete-icon': {
                        opacity: 1,
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={food.image}
                      alt={food.name}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    <Box
                      className="delete-icon"
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        bgcolor: 'rgba(0, 0, 0, 0.6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        opacity: 0,
                        transition: 'opacity 0.2s',
                        '&:hover': {
                          bgcolor: 'rgba(0, 0, 0, 0.8)',
                        },
                      }}
                      onClick={() => setRecommendedFoods((prev) => prev.filter((_, i) => i !== index))}
                    >
                      <CloseIcon sx={{ fontSize: 14, color: 'white' }} />
                    </Box>
                  </Box>
                ))}
                {recommendedFoods.length < maxRecommendQuantity && (
                  <Box
                    onClick={() => setOpenRecommendedModal(true)}
                    sx={{
                      width: 80,
                      height: 80,
                      border: '2px dashed #10B981',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      bgcolor: '#F0FDF4',
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: '#D1FAE5',
                        borderColor: '#059669',
                      },
                    }}
                  >
                    <AddIcon sx={{ fontSize: 32, color: '#10B981' }} />
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box className={classes.BoxFooter}>
        {data ? (
          <Box className={classes.BoxBtn}>
            <Button
              id="remove_button"
              fullWidth={false}
              type="button"
              title={t(LANG_TEXT.REMOVE)}
              buttonStyle={buttonRemoveStyle}
              sxTextStyle={{ color: 'var(--Error-700, #B42318)', fontSize: '18px', fontWeight: '600', lineHeight: '28px' }}
              onClick={handleOpenModal}
            />
            <Button
              id="save_button"
              fullWidth={false}
              title={t(LANG_TEXT.SAVE)}
              buttonStyle={buttonAddStyle}
              sxTextStyle={{ color: 'var(--Base-White, #FFF)', fontSize: '18px', fontWeight: '600', lineHeight: '28px' }}
            />
          </Box>
        ) : (
          <Button
            id="add_button"
            fullWidth={false}
            title={t(LANG_TEXT.ADD_NEW)}
            buttonStyle={buttonAddStyle}
            sxTextStyle={{ color: 'var(--Base-White, #FFF)', fontSize: '18px', fontWeight: '600', lineHeight: '28px' }}
          />
        )}
      </Box>
      <RemoveItemModal open={openModal} handleClose={handleCloseModal} handleDelete={() => handleRemoveFood()} />
      <AddRecommendedModal
        open={openRecommendedModal}
        onClose={() => setOpenRecommendedModal(false)}
        onAdd={(items) => setRecommendedFoods(items)}
        currentRecommendations={recommendedFoods}
      />
    </form>
  );
}

export default AddFood;
